import { useState, useEffect } from 'react';
import { SurveyQuestion } from '../services/surveyBackendService';
import Selector from './common/Selector';
import AnswerSelector from './AnswerSelector';

interface KeywordsProps {
  onAnswersChange: (answers: Record<number, string | string[]>) => void;
  questions: SurveyQuestion[];
  originalSurvey?: { questions: SurveyQuestion[] };
  answers?: Record<number, string | string[]>;
}

const Keywords = ({
  onAnswersChange,
  questions,
  originalSurvey,
  answers: propAnswers = {},
}: KeywordsProps) => {
  const [expandedQuestion, setExpandedQuestion] = useState<number | null>(null);
  const [answers, setAnswers] = useState<Record<number, string | string[]>>(propAnswers);

  useEffect(() => {
    setAnswers(propAnswers);
  }, [propAnswers]);

  const handleQuestionClick = (questionIndex: number) => {
    setExpandedQuestion((prev) => (prev === questionIndex ? null : questionIndex));
  };

  const handleAnswerClick = (questionIndex: number, answer: string) => {
    const question = questions[questionIndex];
    if (!question) return;

    const originalIndex = originalSurvey
      ? originalSurvey.questions.findIndex(
          (q) =>
            q.mainText === question.mainText &&
            q.helpText === question.helpText &&
            q.section === question.section
        )
      : questionIndex;

    if (question.questionType === 'multipleChoice') {
      const current = (answers[originalIndex] as string[]) || [];
      const exists = current.includes(answer);
      const next = exists ? current.filter((a) => a !== answer) : [...current, answer];

      const newState = { ...answers };
      if (next.length > 0) newState[originalIndex] = next;
      else delete newState[originalIndex];

      setAnswers(newState);
      onAnswersChange(newState);
    } else {
      const newState = { ...answers, [originalIndex]: answer };

      setAnswers(newState);
      onAnswersChange(newState);
      setExpandedQuestion(null);
    }
  };

  return (
    <div className="w-full space-x-2 space-y-2">
      {questions.map((question, questionIndex) => {
        const isExpanded = expandedQuestion === questionIndex;

        const originalIndex = originalSurvey
          ? originalSurvey.questions.findIndex(
              (q) =>
                q.mainText === question.mainText &&
                q.helpText === question.helpText &&
                q.section === question.section
            )
          : questionIndex;

        const value = answers[originalIndex];
        const hasAnswer = value && (Array.isArray(value) ? value.length > 0 : true);

        return (
          <div key={`question-${originalIndex}`} className={isExpanded ? 'w-full' : 'inline-block'}>
            {isExpanded ? (
              <AnswerSelector
                question={question}
                questionIndex={questionIndex}
                selectedAnswers={value || (question.questionType === 'multipleChoice' ? [] : '')}
                onAnswerClick={handleAnswerClick}
                onClose={() => setExpandedQuestion(null)}
              />
            ) : (
              <Selector
                onClick={() => handleQuestionClick(questionIndex)}
                text={question.helpText || ''}
                variant={hasAnswer ? 'selected' : 'default'}
                selectionCount={Array.isArray(value) ? value.length : undefined}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

export default Keywords;
